import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from "react";
import { IncomingGoodDetailsService } from "../../../services/logistic/incomingGoodDetails.ts";
import { ErrorResponse } from "../../../Models/Error.ts";
import { BarcodeMovement } from "../../../Models/BarcodeMovement.ts";
import { callErrorToast } from "../../../utilities/utilities.ts";

const incomingGoodDetailsService = new IncomingGoodDetailsService();

interface BarcodeMovementsProviderProps {
  children: ReactNode;
  barcode: string;
}

interface BarcodeMovementsContextValue {
  data: BarcodeMovement[];
  loader: boolean;
}

const BarcodeMovementsContext = createContext<
  BarcodeMovementsContextValue | undefined
>(undefined);

const BarcodeMovementsProvider: React.FC<BarcodeMovementsProviderProps> = ({
  children,
  barcode,
}) => {
  const [data, setData] = useState<BarcodeMovement[]>([]);
  const [error, setError] = useState<ErrorResponse<BarcodeMovement> | null>(
    null
  );
  const [loader, setLoader] = useState<boolean>(true);

  const getMovements = (barcode: string): void => {
    incomingGoodDetailsService
      .barcodeMovements(barcode)
      .then((res) => {
        setData(res.data);
        setLoader(false);
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });
  };

  useEffect(() => {
    getMovements(barcode);
  }, []);

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  return (
    <BarcodeMovementsContext.Provider value={{ data, loader }}>
      {children}
    </BarcodeMovementsContext.Provider>
  );
};

const useBarcodeMovementsContext = (): BarcodeMovementsContextValue => {
  const context = useContext(BarcodeMovementsContext);
  if (!context) {
    throw new Error(
      "useBarcodeMovementsContext must be used within an BarcodeMovementsProvider"
    );
  }
  return context;
};

export { BarcodeMovementsProvider, useBarcodeMovementsContext };
